import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, TrendingUp, MapPin, Layers, MousePointerClick, BarChart3 } from "lucide-react";

const WINDOWS = [
    { value: 7, label: "Last 7 days" },
    { value: 30, label: "Last 30 days" },
    { value: 90, label: "Last 90 days" },
];

function StatCard({ label, value, sub, icon: Icon }) {
    return (
        <div className="ll-card">
            <div className="flex items-start justify-between">
                <div>
                    <div className="ll-label">{label}</div>
                    <div className="font-serif text-3xl font-semibold mt-1 tabular-nums" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
                    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
                </div>
                {Icon && <Icon className="h-5 w-5 text-primary opacity-60" />}
            </div>
        </div>
    );
}

function RankList({ items, emptyText, testIdPrefix, valueLabel = "questions" }) {
    if (!items || items.length === 0) {
        return <div className="text-sm text-muted-foreground italic" data-testid={`${testIdPrefix}-empty`}>{emptyText}</div>;
    }
    const max = Math.max(...items.map((i) => i.count), 1);
    return (
        <ol className="space-y-2">
            {items.map((item, idx) => (
                <li key={item.key || idx} className="flex items-center gap-3 text-sm" data-testid={`${testIdPrefix}-row-${idx}`}>
                    <span className="w-6 text-right font-semibold text-muted-foreground tabular-nums shrink-0">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                            <span className="truncate font-medium">{item.name || item.key || "—"}</span>
                            <span className="tabular-nums font-semibold">{item.count.toLocaleString()}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${Math.round((item.count / max) * 100)}%` }} />
                        </div>
                    </div>
                </li>
            ))}
        </ol>
    );
}

function MiniBarTrend({ daily, days }) {
    if (!daily || daily.length === 0) {
        return <div className="text-xs text-muted-foreground italic">No daily activity in this window.</div>;
    }
    const max = Math.max(...daily.map((d) => d.count), 1);
    return (
        <div className="flex items-end gap-0.5 h-20" data-testid="trend-bars">
            {daily.map((d) => (
                <div key={d.date} className="flex-1 bg-primary/70 hover:bg-primary rounded-t transition-colors" style={{ height: `${Math.max(4, (d.count / max) * 100)}%` }} title={`${d.date}: ${d.count}`} />
            ))}
        </div>
    );
}

export default function AdminAnalyticsTab() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/admin/analytics?days=${days}`)
            .then(({ data }) => { if (!cancelled) setData(data); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [days]);

    const t = data?.totals;
    const sel = data?.selected;

    return (
        <div data-testid="admin-analytics">
            <p className="text-sm text-muted-foreground mb-4">
                Real demand data: which legal categories and locations are getting asked about. Use this to
                prioritize attorney outreach and as proof for your sales pitch.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard label="Last 7 days" value={t?.last_7_days?.questions?.toLocaleString() ?? "—"} sub={`${t?.last_7_days?.clicks ?? 0} attorney clicks`} icon={TrendingUp} />
                <StatCard label="Last 30 days" value={t?.last_30_days?.questions?.toLocaleString() ?? "—"} sub={`${t?.last_30_days?.clicks ?? 0} attorney clicks`} icon={TrendingUp} />
                <StatCard label="Last 90 days" value={t?.last_90_days?.questions?.toLocaleString() ?? "—"} sub={`${t?.last_90_days?.clicks ?? 0} attorney clicks`} icon={TrendingUp} />
                <StatCard label="All time" value={t?.all_time?.questions?.toLocaleString() ?? "—"} sub={`${t?.all_time?.clicks ?? 0} attorney clicks`} icon={BarChart3} />
            </div>

            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="analytics-window-toggle">
                <span className="ll-label mr-2">Window:</span>
                {WINDOWS.map((w) => (
                    <button key={w.value} onClick={() => setDays(w.value)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                                days === w.value
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background text-muted-foreground border-border hover:border-primary/40"
                            }`}
                            data-testid={`window-${w.value}`}>
                        {w.label}
                    </button>
                ))}
                {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-2" />}
            </div>

            {sel && (
                <>
                    <div className="ll-card mb-6">
                        <div className="flex items-center justify-between gap-4 mb-3">
                            <div>
                                <div className="ll-label">Daily question volume</div>
                                <div className="text-xs text-muted-foreground">{sel.total_questions.toLocaleString()} questions · {sel.total_attorney_clicks.toLocaleString()} attorney clicks · last {sel.window_days} days</div>
                            </div>
                        </div>
                        <MiniBarTrend daily={sel.daily_trend} days={sel.window_days} />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="ll-card">
                            <div className="flex items-center gap-2 mb-3">
                                <Layers className="h-4 w-4 text-primary" />
                                <h3 className="font-serif text-lg font-semibold">Top practice areas (questions)</h3>
                            </div>
                            <RankList items={sel.by_practice_area} emptyText="No questions yet in this window." testIdPrefix="rank-practice" />
                        </div>
                        <div className="ll-card">
                            <div className="flex items-center gap-2 mb-3">
                                <MousePointerClick className="h-4 w-4 text-primary" />
                                <h3 className="font-serif text-lg font-semibold">Buyer-intent: attorney clicks by category</h3>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                                Which categories drive consumers to actually click "Find an attorney." Best signal for paid demand.
                            </p>
                            <RankList items={sel.by_practice_area_clicks} emptyText="No attorney clicks yet in this window." testIdPrefix="rank-clicks" valueLabel="clicks" />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="ll-card">
                            <div className="flex items-center gap-2 mb-3">
                                <MapPin className="h-4 w-4 text-primary" />
                                <h3 className="font-serif text-lg font-semibold">Top states</h3>
                            </div>
                            <RankList items={sel.by_state} emptyText="No location data yet — early traffic may be ZIPs outside coverage." testIdPrefix="rank-state" />
                        </div>
                        <div className="ll-card">
                            <div className="flex items-center gap-2 mb-3">
                                <MapPin className="h-4 w-4 text-primary" />
                                <h3 className="font-serif text-lg font-semibold">Top counties</h3>
                            </div>
                            <RankList items={sel.by_county} emptyText="No county-level activity yet." testIdPrefix="rank-county" />
                        </div>
                    </div>
                </>
            )}
            {!sel && !loading && (
                <div className="ll-card text-sm text-muted-foreground" data-testid="analytics-no-data">
                    No analytics data available yet. Once consumers ask questions, this view will populate.
                </div>
            )}
        </div>
    );
}
